/**
 * What kind of thing the card stores. Vocabulary apps usually assume "word",
 * but the matrix needs to host phrases, idioms, grammar patterns, formulas,
 * dialogues, etc. — each has different SRS and rendering implications.
 */

import type { Localized } from "./locale";

export type EntityTypeId =
  | "word"
  | "phrase"
  | "idiom"
  | "grammar_pattern"
  | "semantic_concept"
  | "formula"
  | "dialogue"
  | "scenario"
  | "cultural_context"
  | "pronunciation";

export type EntityTypeRef = {
  id: EntityTypeId;
  name: Localized;
  hint: Localized;
  /** Lucide icon name (resolved at render time). */
  icon: string;
};

export const ENTITY_TYPES: EntityTypeRef[] = [
  {
    id: "word",
    name: { ru: "Слово", en: "Word", es: "Palabra", fr: "Mot", de: "Wort", it: "Parola", zh: "单词", ja: "単語" },
    hint: { ru: "Отдельная лексема", en: "A single lexical unit", es: "Una unidad léxica", fr: "Une unité lexicale", de: "Eine einzelne Lexem", it: "Una singola unità lessicale", zh: "单一词项", ja: "単一の語" },
    icon: "Type",
  },
  {
    id: "phrase",
    name: { ru: "Фраза", en: "Phrase", es: "Frase", fr: "Expression", de: "Phrase", it: "Frase", zh: "短语", ja: "フレーズ" },
    hint: { ru: "Устойчивая комбинация (н-р, «принять решение»)", en: "Set phrase (e.g., \"make a decision\")", es: "Frase fija (p. ej., «tomar una decisión»)", fr: "Expression figée (p. ex., « prendre une décision »)", de: "Festes Wortgefüge (z. B. „eine Entscheidung treffen“)", it: "Espressione fissa (es. «prendere una decisione»)", zh: "固定搭配（如「做决定」）", ja: "定型表現（例：「決定する」）" },
    icon: "MessageSquare",
  },
  {
    id: "idiom",
    name: { ru: "Идиома", en: "Idiom", es: "Modismo", fr: "Idiome", de: "Idiom", it: "Idioma", zh: "习语", ja: "イディオム" },
    hint: { ru: "Не разбирается по смыслу составных слов", en: "Meaning not derivable from individual words", es: "Significado no deducible de las palabras", fr: "Sens non déductible des mots individuels", de: "Bedeutung nicht aus Einzelwörtern ableitbar", it: "Significato non deducibile dalle singole parole", zh: "无法由组成词推断出意义", ja: "個々の単語からは意味を導けない表現" },
    icon: "Sparkles",
  },
  {
    id: "grammar_pattern",
    name: { ru: "Грамматика", en: "Grammar pattern", es: "Patrón gramatical", fr: "Schéma grammatical", de: "Grammatikmuster", it: "Schema grammaticale", zh: "语法结构", ja: "文法パターン" },
    hint: { ru: "Конструкция или паттерн (н-р, Present Perfect)", en: "Structure or pattern (e.g., Present Perfect)", es: "Estructura o patrón (p. ej., Present Perfect)", fr: "Structure ou schéma (p. ex., Present Perfect)", de: "Struktur oder Muster (z. B. Present Perfect)", it: "Struttura o schema (es. Present Perfect)", zh: "结构或模式（如 Present Perfect）", ja: "構文や型（例：Present Perfect）" },
    icon: "BookOpen",
  },
  {
    id: "semantic_concept",
    name: { ru: "Концепт", en: "Concept", es: "Concepto", fr: "Concept", de: "Konzept", it: "Concetto", zh: "概念", ja: "概念" },
    hint: { ru: "Абстрактное понятие, общее для языков", en: "Abstract notion shared across languages", es: "Noción abstracta común a varios idiomas", fr: "Notion abstraite commune à plusieurs langues", de: "Sprachübergreifender abstrakter Begriff", it: "Nozione astratta comune a più lingue", zh: "跨语言的抽象概念", ja: "言語を越えた抽象概念" },
    icon: "Network",
  },
  {
    id: "formula",
    name: { ru: "Формула", en: "Formula", es: "Fórmula", fr: "Formule", de: "Formel", it: "Formula", zh: "公式", ja: "数式" },
    hint: { ru: "STEM-выражение или универсальное обозначение", en: "STEM expression or universal notation", es: "Expresión STEM o notación universal", fr: "Expression STEM ou notation universelle", de: "STEM-Ausdruck oder universelle Notation", it: "Espressione STEM o notazione universale", zh: "STEM 表达式或通用记号", ja: "STEM表現または共通記法" },
    icon: "Sigma",
  },
  {
    id: "dialogue",
    name: { ru: "Диалог", en: "Dialogue", es: "Diálogo", fr: "Dialogue", de: "Dialog", it: "Dialogo", zh: "对话", ja: "ダイアログ" },
    hint: { ru: "Готовый разговорный сценарий", en: "Ready-made conversation script", es: "Guion de conversación", fr: "Scénario de conversation", de: "Vorgefertigter Gesprächsablauf", it: "Copione di conversazione", zh: "现成的对话脚本", ja: "用意された会話の流れ" },
    icon: "MessagesSquare",
  },
  {
    id: "scenario",
    name: { ru: "Сценарий", en: "Scenario", es: "Escenario", fr: "Scénario", de: "Szenario", it: "Scenario", zh: "情景", ja: "シナリオ" },
    hint: { ru: "Ситуативная заготовка (н-р, собеседование)", en: "Situational template (e.g., a job interview)", es: "Plantilla situacional (p. ej., entrevista)", fr: "Modèle situationnel (p. ex., entretien d'embauche)", de: "Situatives Muster (z. B. Vorstellungsgespräch)", it: "Modello situazionale (es. colloquio)", zh: "情景模板（如面试）", ja: "状況テンプレート（例：面接）" },
    icon: "Drama",
  },
  {
    id: "cultural_context",
    name: { ru: "Культурный контекст", en: "Cultural context", es: "Contexto cultural", fr: "Contexte culturel", de: "Kultureller Kontext", it: "Contesto culturale", zh: "文化语境", ja: "文化的文脈" },
    hint: { ru: "Норма, вежливость, региональная специфика", en: "Norms, etiquette, regional specifics", es: "Normas, cortesía, especificidades regionales", fr: "Normes, politesse, spécificités régionales", de: "Normen, Höflichkeit, regionale Besonderheiten", it: "Norme, cortesia, specificità regionali", zh: "礼仪、规范与地区差异", ja: "規範・礼儀・地域差" },
    icon: "Globe2",
  },
  {
    id: "pronunciation",
    name: { ru: "Произношение", en: "Pronunciation", es: "Pronunciación", fr: "Prononciation", de: "Aussprache", it: "Pronuncia", zh: "发音", ja: "発音" },
    hint: { ru: "Фонетика, ударение, интонация", en: "Phonetics, stress, intonation", es: "Fonética, acento, entonación", fr: "Phonétique, accentuation, intonation", de: "Phonetik, Betonung, Intonation", it: "Fonetica, accento, intonazione", zh: "语音、重音与语调", ja: "音声・アクセント・抑揚" },
    icon: "Volume2",
  },
];

export const ENTITY_TYPE_BY_ID: Record<EntityTypeId, EntityTypeRef> = Object.fromEntries(
  ENTITY_TYPES.map((e) => [e.id, e]),
) as Record<EntityTypeId, EntityTypeRef>;

export function isEntityTypeId(value: unknown): value is EntityTypeId {
  return typeof value === "string" && value in ENTITY_TYPE_BY_ID;
}
